/**
 * Change WO (Weekly Off override) helpers — mirror Change Shift desk flow.
 * Cap counts only full-day WO dates; approved Change WO swaps current WO → working day
 * and proposed date → WO.
 */

import { getLocalDayKey } from "./attendancePunchStorage";
import {
  defaultLeaveGroupWorkflow,
  normalizeLeaveGroupWorkflow,
} from "./leaveApprovalWorkflow";
import { findLockedAttendanceDate } from "./attendanceMonthLock";

export const CHANGE_WO_ACTION_LABEL = "Change WO";

const WO_POLICIES_CACHE_KEY = "hrms_weekly_off_policies_cache_v1";
const WO_MAPPINGS_CACHE_KEY = "hrms_employee_weekly_off_mappings_cache_v1";

const ROLE_LABELS = {
  MANAGER: "Reporting Manager",
  EMPLOYEE: "Employee / Self",
  reporting_manager: "Reporting Manager",
  manager: "Manager",
  hr: "HR",
  management: "Management",
  admin: "Admin",
};

export function defaultWeeklyOffOverrideWorkflow() {
  return normalizeLeaveGroupWorkflow({
    ...defaultLeaveGroupWorkflow(),
    approvalMethod: "workflow",
  });
}

export function normalizeWeeklyOffOverrideWorkflow(raw) {
  if (!raw || typeof raw !== "object") return defaultWeeklyOffOverrideWorkflow();
  return normalizeLeaveGroupWorkflow(raw);
}

export function parseOverrideWorkflowJson(raw) {
  if (raw == null || raw === "") return defaultWeeklyOffOverrideWorkflow();
  if (typeof raw === "object") return normalizeWeeklyOffOverrideWorkflow(raw);
  try {
    return normalizeWeeklyOffOverrideWorkflow(JSON.parse(raw));
  } catch {
    return defaultWeeklyOffOverrideWorkflow();
  }
}

export function cacheWeeklyOffStructureData(policies, mappings) {
  try {
    if (Array.isArray(policies)) {
      localStorage.setItem(WO_POLICIES_CACHE_KEY, JSON.stringify(policies));
    }
    if (mappings && typeof mappings === "object") {
      localStorage.setItem(WO_MAPPINGS_CACHE_KEY, JSON.stringify(mappings));
    }
  } catch {
    /* ignore */
  }
}

export function loadCachedWeeklyOffPolicies() {
  try {
    const raw = localStorage.getItem(WO_POLICIES_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function loadCachedWeeklyOffMappings() {
  try {
    const raw = localStorage.getItem(WO_MAPPINGS_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function employeeIdKey(employee) {
  if (employee == null) return null;
  if (typeof employee === "object") {
    const id = employee.id ?? employee.employeeId ?? employee.empId;
    return id != null ? String(id) : null;
  }
  return String(employee);
}

function toDayKey(dateOrKey) {
  if (!dateOrKey) return getLocalDayKey();
  if (typeof dateOrKey === "string" && /^\d{4}-\d{2}-\d{2}/.test(dateOrKey)) {
    return dateOrKey.slice(0, 10);
  }
  if (dateOrKey instanceof Date && !isNaN(dateOrKey.getTime())) {
    const y = dateOrKey.getFullYear();
    const m = String(dateOrKey.getMonth() + 1).padStart(2, "0");
    const d = String(dateOrKey.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  return String(dateOrKey).slice(0, 10);
}

function dayNameFromKey(dayKey) {
  const d = new Date(`${dayKey}T12:00:00`);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
}

function monthPrefix(dayKey) {
  return String(dayKey || "").slice(0, 7);
}

export function resolveEmployeeWeeklyOffPolicy(
  employee,
  weeklyOffPolicies = null,
  employeeWeeklyOffMappings = null
) {
  const policies = weeklyOffPolicies ?? loadCachedWeeklyOffPolicies();
  const mappings = employeeWeeklyOffMappings ?? loadCachedWeeklyOffMappings();
  const empKey = employeeIdKey(employee);
  if (!empKey || !Array.isArray(policies) || policies.length === 0) return null;

  const mapping = mappings[empKey] || mappings[Number(empKey)];
  let policyIds = [];
  if (Array.isArray(mapping)) policyIds = mapping;
  else if (mapping?.policies) policyIds = mapping.policies;
  else if (mapping?.policyId != null) policyIds = [mapping.policyId];

  const idSet = new Set(policyIds.map((id) => Number(id)).filter((n) => !Number.isNaN(n)));
  const matched =
    policies.find((p) => idSet.has(Number(p.id))) ||
    policies.find((p) => p.isActive !== false && p.weeklyOffs);

  if (!matched) return null;

  const overrideFromMapping =
    mapping &&
    typeof mapping === "object" &&
    !Array.isArray(mapping) &&
    (mapping.allowOverride === true ||
      mapping.overridePolicy === true ||
      mapping.allowOverride === "true" ||
      mapping.overridePolicy === "true");

  return {
    ...matched,
    overridePolicy:
      matched.overridePolicy === true ||
      matched.allowOverride === true ||
      !!overrideFromMapping,
    overrideWorkflow: normalizeWeeklyOffOverrideWorkflow(
      matched.overrideWorkflow ||
        parseOverrideWorkflowJson(matched.overrideWorkflowJson)
    ),
    maxFullDayWoChangePerMonth:
      matched.maxFullDayWoChangePerMonth != null
        ? Number(matched.maxFullDayWoChangePerMonth)
        : null,
  };
}

export function isChangeWoEnabledForEmployee(
  employee,
  weeklyOffPolicies = null,
  employeeWeeklyOffMappings = null
) {
  const policy = resolveEmployeeWeeklyOffPolicy(
    employee,
    weeklyOffPolicies,
    employeeWeeklyOffMappings
  );
  return !!(policy && policy.overridePolicy);
}

export function resolveWeeklyOffTypeForDay(
  employee,
  dayKey,
  weeklyOffPolicies = null,
  employeeWeeklyOffMappings = null
) {
  const policy = resolveEmployeeWeeklyOffPolicy(
    employee,
    weeklyOffPolicies,
    employeeWeeklyOffMappings
  );
  if (!policy?.weeklyOffs) return false;
  const dayName = dayNameFromKey(toDayKey(dayKey));
  if (!dayName) return false;
  const cfg = policy.weeklyOffs[dayName];
  if (!cfg?.enabled) return false;
  return cfg.type || "full";
}

export function isFullDayWeeklyOff(
  employee,
  dayKey,
  weeklyOffPolicies = null,
  employeeWeeklyOffMappings = null
) {
  return (
    resolveWeeklyOffTypeForDay(
      employee,
      dayKey,
      weeklyOffPolicies,
      employeeWeeklyOffMappings
    ) === "full"
  );
}

function loadDeskRequests() {
  try {
    const raw = localStorage.getItem("adminQuickActionRequests");
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function listChangeWoRequestsForEmployee(employeeId) {
  const id = Number(employeeId);
  if (!Number.isFinite(id)) return [];
  return loadDeskRequests().filter((r) => {
    if (!r || r.actionLabel !== CHANGE_WO_ACTION_LABEL) return false;
    return Number(r.employeeId) === id;
  });
}

export function countFullDayChangeWoInMonth(employeeId, yearMonth) {
  const prefix = String(yearMonth || "").slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(prefix)) return 0;
  return listChangeWoRequestsForEmployee(employeeId).filter((r) => {
    const status = String(r.status || "");
    if (status !== "Pending" && status !== "Approved") return false;
    const date = String(r?.formData?.date || "").slice(0, 10);
    if (!date.startsWith(prefix)) return false;
    const woType = String(r?.formData?.weeklyOffType || "full").toLowerCase();
    return woType === "full";
  }).length;
}

export function validateChangeWoRequest({
  employee,
  date,
  proposedDate = null,
  weeklyOffPolicies = null,
  employeeWeeklyOffMappings = null,
  excludeRequestId = null,
  attendanceLocks = null,
  processedPayMonths = null,
}) {
  const dayKey = toDayKey(date);
  if (!dayKey) return { ok: false, message: "Select the current Weekly Off date." };

  const proposedKey = proposedDate ? toDayKey(proposedDate) : "";
  if (!proposedKey) {
    return { ok: false, message: "Select the proposed Weekly Off date." };
  }
  if (proposedKey === dayKey) {
    return { ok: false, message: "Proposed Weekly Off must be a different date." };
  }

  const policy = resolveEmployeeWeeklyOffPolicy(
    employee,
    weeklyOffPolicies,
    employeeWeeklyOffMappings
  );
  if (!policy?.overridePolicy) {
    return {
      ok: false,
      message: "Change WO is not enabled for this employee's Weekly Off policy.",
    };
  }

  const woType = resolveWeeklyOffTypeForDay(
    employee,
    dayKey,
    weeklyOffPolicies,
    employeeWeeklyOffMappings
  );
  if (woType !== "full") {
    return {
      ok: false,
      message:
        woType === "first_half" || woType === "second_half"
          ? "Change WO applies only to full-day Weekly Off dates (half-day WO is not eligible)."
          : "Current date is not a Weekly Off for this employee.",
    };
  }

  if (!hasApprovedChangeWoForDay(employeeIdKey(employee), proposedKey)) {
    const proposedWoType = resolveWeeklyOffTypeForDay(
      employee,
      proposedKey,
      weeklyOffPolicies,
      employeeWeeklyOffMappings
    );
    if (proposedWoType) {
      return {
        ok: false,
        message: "Proposed date is already a Weekly Off. Pick a working day.",
      };
    }
  }

  const empId = employeeIdKey(employee);
  const dup = listChangeWoRequestsForEmployee(empId).find((r) => {
    if (excludeRequestId && String(r.requestId) === String(excludeRequestId)) return false;
    const status = String(r.status || "");
    if (status !== "Pending" && status !== "Approved") return false;
    const cur = String(r?.formData?.date || "").slice(0, 10);
    const prop = String(r?.formData?.proposedDate || "").slice(0, 10);
    return cur === dayKey || prop === dayKey || cur === proposedKey || prop === proposedKey;
  });
  if (dup) {
    return {
      ok: false,
      message: `A Change WO request already exists involving one of these dates (${dup.status}).`,
    };
  }

  const locked = findLockedAttendanceDate(attendanceLocks, [dayKey, proposedKey]);
  if (locked) {
    return {
      ok: false,
      message: `Attendance is locked for ${locked.slice(0, 7)}. Unlock the month before Change WO.`,
    };
  }

  const processedMonths = Array.isArray(processedPayMonths) ? processedPayMonths : [];
  const processedHit = [dayKey, proposedKey]
    .map((d) => String(d || "").slice(0, 7))
    .find((ym) => processedMonths.includes(ym));
  if (processedHit) {
    return {
      ok: false,
      message: `${processedHit} is processed for this employee. Unprocess Attendance before Change WO.`,
    };
  }

  const cap = policy.maxFullDayWoChangePerMonth;
  if (cap != null && Number.isFinite(Number(cap)) && Number(cap) >= 0) {
    const used = countFullDayChangeWoInMonth(empId, monthPrefix(dayKey));
    if (used >= Number(cap)) {
      return {
        ok: false,
        message: `Monthly Change WO limit reached (${cap} full-day Weekly Off day${
          Number(cap) === 1 ? "" : "s"
        } this month).`,
      };
    }
  }

  return {
    ok: true,
    policy,
    dayKey,
    proposedKey,
    weeklyOffType: "full",
  };
}

export function resolveChangeWoPendingWith(policyOrWorkflow) {
  const workflow =
    policyOrWorkflow?.overrideWorkflow ||
    (policyOrWorkflow?.approvalMethod
      ? normalizeWeeklyOffOverrideWorkflow(policyOrWorkflow)
      : null);
  if (!workflow) return "Reporting Manager";
  const method =
    workflow.approvalMethod === "on_instance"
      ? "auto_approved"
      : workflow.approvalMethod || "workflow";
  if (method === "auto_approved") return null;
  const steps = Array.isArray(workflow.workflowSteps) ? workflow.workflowSteps : [];
  const first = steps.find((s) => s && !s.notifyOnly);
  if (!first?.role) return "Reporting Manager";
  return (
    ROLE_LABELS[first.role] ||
    String(first.role)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

export function isChangeWoAutoApproved(policy) {
  const workflow = policy?.overrideWorkflow || defaultWeeklyOffOverrideWorkflow();
  const method =
    workflow.approvalMethod === "on_instance"
      ? "auto_approved"
      : workflow.approvalMethod || "workflow";
  return method === "auto_approved";
}

/** Former WO date after approved Change WO → working full day. */
export function hasApprovedChangeWoForDay(employeeId, dayKey = getLocalDayKey()) {
  const id = Number(employeeId);
  const key = toDayKey(dayKey);
  if (!Number.isFinite(id) || !key) return false;
  return listChangeWoRequestsForEmployee(id).some((r) => {
    if (r.status !== "Approved") return false;
    const fd = r?.formData || {};
    const current = String(fd.date || fd.currentWoDate || "").slice(0, 10);
    return current === key;
  });
}

/** Proposed WO date after approved Change WO → treat as Weekly Off. */
export function hasApprovedProposedWoForDay(employeeId, dayKey = getLocalDayKey()) {
  const id = Number(employeeId);
  const key = toDayKey(dayKey);
  if (!Number.isFinite(id) || !key) return false;
  return listChangeWoRequestsForEmployee(id).some((r) => {
    if (r.status !== "Approved") return false;
    const fd = r?.formData || {};
    const proposed = String(fd.proposedDate || fd.proposedWoDate || "").slice(0, 10);
    return proposed === key;
  });
}
